import { useEffect } from 'react';
import { X } from '../../icons/wp-icons';
import { motion, AnimatePresence } from 'framer-motion';

// Size-Klassen für verschiedene Modal-Größen
const SIZE_CLASSES = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl'
};

// Animation-Varianten
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    scale: 1
  },
  exit: {
    opacity: 0,
    scale: 0.95
  }
};

function Modal({ isOpen, onClose, title, children, size = 'md' }) {
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  // Escape-Taste zum Schließen
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto overflow-x-hidden">
          {/* Animated Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.15, ease: "easeOut" }}
            onClick={onClose}
          />

          {/* Modal Container - Höher positioniert auf Mobile wegen Bottom-Navigation */}
          <div
            className="flex min-h-full items-start sm:items-center justify-center p-3 sm:p-4 pt-6 sm:pt-4"
            style={{
              paddingBottom: 'calc(16px + var(--bottom-nav-height, 64px) + var(--safe-area-bottom, 0px) + 16px)'
            }}
          >
            {/* Animated Modal Content */}
            <motion.div
              className={`relative bg-surface rounded-card shadow-card w-full ${sizeClass} p-4 sm:p-6 max-h-[calc(100vh-80px-var(--bottom-nav-height,64px)-env(safe-area-inset-bottom,0px))] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden`}
              style={{ WebkitOverflowScrolling: 'touch' }}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{
                duration: 0.15,
                ease: "easeOut",
                exit: { duration: 0.1, ease: "easeIn" }
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4 sm:mb-6 sticky top-0 bg-surface pb-2 -mt-1 pt-1 z-10">
                <h2 className="text-lg sm:text-2xl font-serif font-bold text-text pr-2">{title}</h2>
                <button
                  onClick={onClose}
                  className="text-text-muted hover:text-text transition p-2 rounded-lg hover:bg-gray-100 -mr-2 shrink-0"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Content - px-2 für Focus-Ring der Inputs */}
              <div className="overflow-x-hidden px-2 -mx-2">{children}</div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default Modal;
